/**
 * Typed doorway to the shared chart palette (Settings → Appearance → Charts).
 *
 * The palette modules are plain JavaScript; like reportsNativeKit.ts this is
 * the one place they cross into TypeScript. Every forecast chart takes its
 * colours from here so the viewer's palette choice reaches the module.
 * Under the Classic shell (and the default "lodge" palette) the values are
 * the same brand colours the charts hardcoded before.
 */

/* eslint-disable @typescript-eslint/no-explicit-any */
// @ts-ignore - JavaScript module without types
import * as Colors from '../../../styles/chartColors';
// @ts-ignore - JavaScript module without types
import * as Theme from '../../../components/ReportsNative/charts/chartTheme';
// @ts-ignore - JavaScript module without types
import { useTheme as useThemeContext } from '../../../context/ThemeContext';


export interface ChartColors {
  primary: string;
  primaryLight: string;
  secondary: string;
  tertiary: string;
  grid: string;
  axis: string;
  positive: string;
  negative: string;
}

export interface ChartTheme {
  dark: boolean;
  paletteId: string;
  colors: ChartColors;
  grid: string;
  tick: string;
  label: string;
  referenceLabel: string;
  tooltip: Record<string, any>;
}

const colors = Colors as unknown as Record<string, any>;
const theme = Theme as unknown as Record<string, any>;

export const chartColorsFor: (dark: boolean, paletteId?: string) => ChartColors = colors.chartColorsFor;
export const isInkShell: () => boolean = colors.isInkShell;
export const LODGE_CHART_PALETTE: string = colors.LODGE_CHART_PALETTE;
export const useChartTheme: () => ChartTheme = theme.useChartTheme;
export const useChartPaletteId: () => string = theme.useChartPaletteId;
export const useIsDarkMode: () => boolean = theme.useIsDarkMode;

/** A lighter wash of a palette colour, for forecast/projected fills. */
export function washOf(color: string, pct = 38): string {
  return `color-mix(in srgb, ${color} ${pct}%, var(--chart-background, var(--lodge-white, #fff)))`;
}


export interface ForecastChartPalette {
  /** New look active: charts follow the viewer's palette. */
  inset: boolean;
  dark: boolean;
  paletteId: string;
  colors: ChartColors;
  /** Lighter fill for months that have not happened yet. */
  forecastFill: string;
}

/**
 * Colours for forecast charts. Classic callers ignore it (inset=false) and
 * keep their historical constants, so the Classic layout renders exactly as
 * before; under the New look the palette from Appearance → Charts applies.
 */
export function useForecastChartPalette(): ForecastChartPalette {
  const { insetActive } = useThemeContext() as { insetActive: boolean };
  const dark = useIsDarkMode();
  const paletteId = useChartPaletteId();
  const colors = chartColorsFor(dark, paletteId);
  const forecastFill = paletteId === LODGE_CHART_PALETTE && !dark ? '#FFC999' : washOf(colors.primary);
  return { inset: !!insetActive, dark, paletteId, colors, forecastFill };
}
